/* @jsx h */
import { Order, PaymentDetails, RestaurantProfile } from "@lemmonhq/types";
import { defineComponent, onMounted, onUnmounted, ref, h, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import {
  fetchPayment,
  fetchCoinOffer,
  claimCoins,
  type CoinOfferResponse,
} from "../../../api/client/index";
import {
  PaymentReceipt,
  CoinOfferCard,
  CoinOfferSkeleton,
  CoinBenefits,
  CoinClaimModal,
} from "../../components";
import { CustomerLanguageSelector } from "../../components/CustomerLanguageSelector";
import { useLocalization } from "@lemmonhq/uikit";

const RECEIPT_REFRESH_INTERVAL_SECONDS = 5;

const bar = (classes: string) => (
  <div class={`rounded skeleton-shimmer ${classes}`} />
);

/**
 * Shaped like the loaded page rather than a centred spinner, so the layout does
 * not jump when the data lands — and so the offer's place in the sidebar is
 * already visible while it loads.
 */
const ReceiptSkeleton = defineComponent({
  name: "ReceiptSkeleton",
  setup() {
    return () => (
      <div class="h-full flex flex-col" aria-busy="true">
        <div class="flex items-center justify-between gap-3 bg-base-100 border-b border-base-300 px-4 py-3">
          {bar("h-6 w-40")}
          {bar("h-8 w-24")}
        </div>
        {/* Matches PaymentReceipt's own wrapper — same min-h-screen, same
            (inherited) ground — so nothing snaps at the handover. */}
        <div class="flex-1 overflow-auto min-h-screen py-6 sm:py-8">
          <div class="container mx-auto px-4 max-w-6xl">
            <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
              <div class="lg:col-span-2 lg:order-1">
                <div class="bg-base-100 rounded-xl border border-base-300 p-6 space-y-5">
                  <div class="flex justify-between gap-4">
                    <div class="space-y-2 flex-1">
                      {bar("h-7 w-32")}
                      {bar("h-4 w-64 max-w-full")}
                    </div>
                    {bar("h-6 w-20")}
                  </div>
                  <div class="space-y-2">
                    {bar("h-5 w-40")}
                    {bar("h-4 w-28")}
                  </div>
                  <div class="border-t border-base-300 pt-5 space-y-3">
                    {[0, 1, 2].map((i) => (
                      <div key={i} class="flex justify-between gap-4">
                        {bar("h-4 w-40")}
                        {bar("h-4 w-20")}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div class="lg:col-span-1 order-first lg:order-2">
                <div class="space-y-4">
                  <div class="bg-base-100 rounded-2xl border border-base-300 p-6 space-y-4">
                    {bar("h-3 w-32")}
                    {bar("h-9 w-40")}
                    {bar("h-4 w-48 max-w-full")}
                    <div class="flex gap-1.5">
                      {[0, 1, 2].map((i) => (
                        <div
                          key={i}
                          class="w-9 h-9 rounded-full skeleton-shimmer"
                        />
                      ))}
                    </div>
                    {bar("h-12 w-full")}
                  </div>
                  <div class="bg-base-100 rounded-xl border border-base-300 p-5 space-y-4">
                    {[0, 1, 2].map((i) => (
                      <div key={i} class="flex items-start gap-3">
                        <div class="w-8 h-8 rounded-lg skeleton-shimmer shrink-0" />
                        <div class="flex-1 space-y-1.5">
                          {bar("h-4 w-3/4")}
                          {bar("h-3 w-1/2")}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  },
});


export const ReceiptPage = defineComponent({
  setup(props) {
    const route = useRoute();
    const router = useRouter();
    const { t } = useLocalization();
    // The coin offer is fetched separately and never blocks the receipt: a
    // broken earn rule must not stop someone reading what they paid.
    const coins = ref<CoinOfferResponse | null>(null);
    // Distinct from `coins === null`, which is also the "no offer" resting
    // state — without this the sidebar would flash a skeleton forever on a
    // receipt that simply has no coins.
    const coinsLoading = ref(true);
    const claiming = ref(false);
    const claimError = ref<string | null>(null);
    // Signed-in claiming is confirmed rather than immediate: a receipt can only
    // be claimed once, by one account, so the account being credited is named
    // before the ledger is written.
    const confirming = ref(false);
    const data = ref<
      | {
          payment: PaymentDetails;
          shop: RestaurantProfile;
          orders?: Order[];
          relatedPayment?: PaymentDetails;
        }
      | Error
      | null
    >(null);
    const countdown = ref(RECEIPT_REFRESH_INTERVAL_SECONDS);
    const pollingInterval = ref<NodeJS.Timeout | null>(null);
    const countdownInterval = ref<NodeJS.Timeout | null>(null);

    const fetchData = async () => {
      const paymentGuid = route.params.paymentGuid as string;
      try {
        const fetchedResult = await fetchPayment(paymentGuid);
        data.value = fetchedResult;
      } catch (e: any) {
        data.value = e;
      }
    };

    const startPolling = () => {
      // Clear any existing intervals
      stopPolling();

      // Reset countdown
      countdown.value = RECEIPT_REFRESH_INTERVAL_SECONDS;

      // Start countdown timer
      countdownInterval.value = setInterval(() => {
        countdown.value -= 1;
        if (countdown.value <= 0) {
          countdown.value = RECEIPT_REFRESH_INTERVAL_SECONDS;
        }
      }, 1000);

      // Poll every N seconds
      pollingInterval.value = setInterval(() => {
        fetchData();
      }, RECEIPT_REFRESH_INTERVAL_SECONDS * 1000);
    };

    const stopPolling = () => {
      if (pollingInterval.value) {
        clearInterval(pollingInterval.value);
        pollingInterval.value = null;
      }
      if (countdownInterval.value) {
        clearInterval(countdownInterval.value);
        countdownInterval.value = null;
      }
    };

    const handleManualRefresh = () => {
      countdown.value = RECEIPT_REFRESH_INTERVAL_SECONDS; // Reset countdown
      fetchData();
    };

    const fetchCoins = async () => {
      const paymentGuid = route.params.paymentGuid as string;
      try {
        coins.value = await fetchCoinOffer(paymentGuid);
      } catch (e) {
        // Silent: no coin section beats a broken receipt page.
        console.warn("Failed to load coin offer:", e);
      } finally {
        coinsLoading.value = false;
      }
    };

    /**
     * Signed in already — no reason to send them through the signup funnel, but
     * the claim still runs behind a confirmation.
     *
     * The server does the rest: it resolves this profile's `customers` row at
     * the shop, creating it on a first visit, and only then mints. A refusal
     * keeps the modal open so the reason is read next to what was on offer;
     * a generic dismissal would read as the coins being taken.
     */
    const handleClaim = async () => {
      claiming.value = true;
      claimError.value = null;
      try {
        const result = await claimCoins(route.params.paymentGuid as string);
        if (result.status === "claimed" || result.status === "already_claimed") {
          coins.value = {
            programs: result.programs,
            claimed: true,
            signed_in: true,
          };
          confirming.value = false;
        } else {
          claimError.value = t(`customer.coins.refused_${result.status}`);
        }
      } catch (e) {
        claimError.value = t("customer.coins.claim_failed");
      } finally {
        claiming.value = false;
      }
    };

    const openConfirm = () => {
      claimError.value = null;
      confirming.value = true;
    };

    const closeConfirm = () => {
      if (claiming.value) return;
      confirming.value = false;
      claimError.value = null;
    };

    const goToSignup = () => {
      router.push({
        path: "/signup",
        query: { payment: route.params.paymentGuid as string },
      });
    };

    // Watch for payment status changes
    watch(
      () => data.value,
      (newData) => {
        if (
          newData &&
          !(newData instanceof Error) &&
          newData.payment.status === "sent"
        ) {
          startPolling();
        } else {
          stopPolling();
        }
      },
      { immediate: true },
    );

    onMounted(() => {
      fetchData();
      fetchCoins();
    });

    // A card payment has no offer until it succeeds, so re-read once it does.
    watch(
      () => (data.value as any)?.payment?.status,
      (status, previous) => {
        if (status && previous && status !== previous) fetchCoins();
      },
    );

    onUnmounted(() => {
      stopPolling();
    });

    /**
     * The whole conversion surface, in the receipt's sidebar — which on mobile
     * is ordered above the receipt, so the offer is the first thing read.
     */
    const renderCoinSection = (shopName?: string) => {
      if (coinsLoading.value) return <CoinOfferSkeleton />;

      const offer = coins.value;
      if (!offer || offer.programs.length === 0) return null;

      const validity = offer.programs[0]?.validity_months ?? null;

      return (
        <div class="space-y-4">
          <CoinOfferCard
            offers={offer.programs}
            shopName={shopName}
            collected={offer.claimed}
            footer={() =>
              offer.claimed ? (
                <div class="space-y-3">
                  <div class="flex items-start gap-2 text-sm text-base-content/60">
                    <span class="material-symbols-outlined text-base text-success shrink-0">
                      verified
                    </span>
                    <span>{t("customer.coins.collected_hint")}</span>
                  </div>
                  <button
                    type="button"
                    class="btn btn-outline btn-sm w-full gap-2"
                    onClick={() => router.push("/collected")}
                  >
                    <span class="material-symbols-outlined text-base">
                      loyalty
                    </span>
                    {t("customer.coins.view_collected")}
                  </button>
                </div>
              ) : (
                <div class="space-y-2">
                  {/* Signed in, the claim is confirmed in a modal; signed out,
                      the account has to exist first. Both start here. */}
                  <button
                    type="button"
                    class="btn btn-primary w-full gap-2 shadow-md"
                    onClick={offer.signed_in ? openConfirm : goToSignup}
                  >
                    <span class="material-symbols-outlined text-lg">redeem</span>
                    {t("customer.coins.claim_cta")}
                  </button>
                  <p class="text-xs text-center text-base-content/50">
                    {offer.signed_in
                      ? t("customer.coins.claim_hint_signed_in")
                      : t("customer.coins.claim_hint")}
                  </p>
                </div>
              )
            }
          />

          {/* Answering "is this worth thirty seconds" only matters before they
              have said yes; once collected it is noise. */}
          {!offer.claimed && (
            <div class="bg-base-100 rounded-xl border border-base-300 p-5">
              <CoinBenefits validityMonths={validity} />
            </div>
          )}
        </div>
      );
    };

    return () => {
      if (data.value === null) {
        return <ReceiptSkeleton />;
      } else if (data.value instanceof Error) {
        return (
          <div class="flex items-center justify-center py-4 h-full">
            <div class="text-error">{t("customer.receipt.failed_to_load")}</div>
          </div>
        );
      } else {
        // Captured so the slot closure keeps the narrowing.
        const view = data.value;
        const isPending = view.payment.status === "sent";

        return (
          <div class="h-full flex flex-col">
            <div class="flex items-center justify-between gap-3 bg-base-100 border-b border-base-300 px-4 py-3">
              <div class="flex items-center gap-2 min-w-0">
                <span class="material-symbols-outlined text-xl text-primary shrink-0">
                  storefront
                </span>
                <span class="font-semibold text-base-content truncate">
                  {view.shop?.name}
                </span>
              </div>
              <CustomerLanguageSelector
                languageIds={["fr", "de", "it", "en"]}
              />
            </div>
            {isPending && (
              <div
                class="bg-warning text-warning-content p-4 text-center cursor-pointer hover:bg-warning/80 transition-colors select-none"
                onClick={handleManualRefresh}
              >
                <div class="flex items-center justify-center gap-2">
                  <span class="loading loading-spinner loading-sm"></span>
                  <span class="font-semibold">
                    {t("customer.receipt.payment_pending_refreshing", [
                      countdown.value,
                    ])}
                  </span>
                  <span class="text-sm opacity-80">
                    {t("customer.receipt.click_to_refresh")}
                  </span>
                </div>
              </div>
            )}
            <div class="flex-1 overflow-auto">
              <PaymentReceipt
                shop={view.shop}
                payment={view.payment}
                orders={view.orders}
                relatedPayment={view.relatedPayment}
                v-slots={{
                  sidebarTop: () => renderCoinSection(view.shop?.name),
                }}
              />
            </div>
            {confirming.value && coins.value && (
              <CoinClaimModal
                offers={coins.value.programs}
                shopName={view.shop?.name}
                claiming={claiming.value}
                error={claimError.value}
                onConfirm={handleClaim}
                onClose={closeConfirm}
              />
            )}
          </div>
        );
      }
    };
  },
});
